"use client";

import { useEffect, useRef } from "react";

import { PWA_INSTALLED_REPORTED_KEY } from "@/constants";
import { useAnalyticsRef } from "@/contexts/analytics-context";
import { usePwaInstall } from "@/contexts/pwa-install-context";
import { getParsedLocalStorageItem, setLocalStorageItem } from "@/hooks/storage";

export const HandlePwaAnalytics = () => {
  const analyticsRef = useAnalyticsRef();
  const { canInstallNatively, installed, displayMode } = usePwaInstall();

  // Fire `installOffered` once on the first false → true edge of
  // `canInstallNatively`. The provider owns the `beforeinstallprompt` listener;
  // we observe its derived state instead of registering a second listener.
  // Chrome can re-fire `beforeinstallprompt` on focus/visibility changes, but
  // the provider's listener is idempotent there — `canInstallNatively` stays
  // true and this effect doesn't re-fire.
  const offeredFiredRef = useRef(false);
  useEffect(() => {
    if (!canInstallNatively || offeredFiredRef.current) return;
    offeredFiredRef.current = true;
    analyticsRef.current.pwa.installOffered();
  }, [canInstallNatively, analyticsRef]);

  // Fire `installed` exactly once per device, regardless of whether the signal
  // came from Chromium's `appinstalled` event (Android/desktop Chrome) or from
  // the first standalone-mode launch (iOS Safari, which never fires the event).
  // PwaInstallProvider sets `installed = true` on either path; we observe that
  // edge here and gate on a localStorage sentinel so a reinstall after the
  // sentinel is cleared correctly fires a fresh event.
  useEffect(() => {
    if (!installed) return;
    if (getParsedLocalStorageItem(PWA_INSTALLED_REPORTED_KEY)) return;
    setLocalStorageItem(PWA_INSTALLED_REPORTED_KEY, true);
    analyticsRef.current.pwa.installed({
      source: displayMode === "standalone" ? "standalone-launch" : "appinstalled-event",
    });
  }, [installed, displayMode, analyticsRef]);

  return null;
};
